import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import {
  ContractPayload,
  ContractTemplate,
  LineItem,
  NetTerms,
  NET_TERMS_OPTIONS,
  DEFAULT_CONTRACT_BODY,
} from "./contractTypes";
import { renderMarkdown, fillPlaceholders } from "./markdownUtils";

// ── Helpers ──────────────────────────────────────────────────────────────

function fmtCurrency(val: number) {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function lineItemsTotal(items: LineItem[]) {
  return items.reduce((s, i) => s + i.qty * i.unitPrice, 0);
}

function serializeLineItems(items: LineItem[]) {
  return JSON.stringify(items);
}

// ── Sub-components ───────────────────────────────────────────────────────

function StepIndicator({ step, total }: { step: number; total: number }) {
  const labels = ["Customer", "Contract", "Pricing", "Review"];
  return (
    <div className="flex items-center gap-0 mb-6">
      {labels.slice(0, total).map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <React.Fragment key={n}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                  ${done ? "bg-green-500 text-white" : active ? "bg-primary text-white" : "bg-slate-200 text-slate-500"}`}
              >
                {done ? "✓" : n}
              </div>
              <span className={`text-xs mt-1 ${active ? "text-primary font-medium" : "text-slate-400"}`}>
                {label}
              </span>
            </div>
            {i < total - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mb-5 ${done ? "bg-green-400" : "bg-slate-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

interface LineItemEditorProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
}

function LineItemEditor({ items, onChange }: LineItemEditorProps) {
  function addRow() {
    onChange([...items, { description: "", qty: 1, unitPrice: 0 }]);
  }

  function updateRow(i: number, patch: Partial<LineItem>) {
    const next = items.map((row, idx) => (idx === i ? { ...row, ...patch } : row));
    onChange(next);
  }

  function removeRow(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }

  const total = lineItemsTotal(items);

  return (
    <div>
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden mb-2">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-slate-600">Description</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600 w-20">Qty</th>
            <th className="text-left px-3 py-2 font-medium text-slate-600 w-28">Unit Price</th>
            <th className="text-right px-3 py-2 font-medium text-slate-600 w-24">Total</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((row, i) => (
            <tr key={i}>
              <td className="px-2 py-1">
                <input
                  type="text"
                  value={row.description}
                  onChange={(e) => updateRow(i, { description: e.target.value })}
                  className="w-full border-0 bg-transparent px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover rounded"
                  placeholder="Service description"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  min={1}
                  value={row.qty}
                  onChange={(e) => updateRow(i, { qty: Number(e.target.value) })}
                  className="w-full border-0 bg-transparent px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover rounded"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={row.unitPrice}
                  onChange={(e) => updateRow(i, { unitPrice: Number(e.target.value) })}
                  className="w-full border-0 bg-transparent px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-hover rounded"
                />
              </td>
              <td className="px-3 py-2 text-right text-slate-700">
                {fmtCurrency(row.qty * row.unitPrice)}
              </td>
              <td className="px-2 py-1 text-center">
                <button
                  onClick={() => removeRow(i)}
                  className="text-slate-300 hover:text-red-400 text-lg leading-none"
                  aria-label="Remove line item"
                >
                  &times;
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          <tr>
            <td colSpan={3} className="px-3 py-2 text-right text-sm font-medium text-slate-600">
              Monthly Subtotal
            </td>
            <td className="px-3 py-2 text-right text-sm font-semibold text-slate-800">
              {fmtCurrency(total)}
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
      <button
        onClick={addRow}
        className="text-sm text-primary hover:underline font-medium"
      >
        + Add line item
      </button>
    </div>
  );
}

// ── ContractEditor main component ─────────────────────────────────────────

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function ContractEditor({ onClose, onCreated }: Props) {
  const api = useApi();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [signingLink, setSigningLink] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Templates
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);

  // Stripe customer search
  const [stripeQuery, setStripeQuery] = useState("");
  const [stripeResults, setStripeResults] = useState<{ id: string; name: string; email: string }[]>([]);
  const [stripeSearching, setStripeSearching] = useState(false);

  // Form state
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerCompany, setCustomerCompany] = useState("");
  const [stripeCustomerId, setStripeCustomerId] = useState("");

  const [title, setTitle] = useState("Master Services Agreement");
  const [body, setBody] = useState(DEFAULT_CONTRACT_BODY);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");

  const [serviceStartDate, setServiceStartDate] = useState("");
  const [serviceEndDate, setServiceEndDate] = useState("");
  const [netTerms, setNetTerms] = useState<NetTerms>("Net 30");
  const [setupFee, setSetupFee] = useState<number>(0);
  const [manualMonthly, setManualMonthly] = useState<number>(0);
  const [useManualMonthly, setUseManualMonthly] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: "Platform subscription", qty: 1, unitPrice: 0 },
  ]);

  const [showBodyPreview, setShowBodyPreview] = useState(false);

  // Load templates
  useEffect(() => {
    api.messaging.get("Contract/templates")
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setTemplates(Array.isArray(d) ? d : []))
      .catch(() => setTemplates([]));
  }, []);

  // Stripe search debounce
  useEffect(() => {
    if (!stripeQuery || stripeQuery.length < 2) {
      setStripeResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setStripeSearching(true);
      try {
        const res = await api.messaging.get(`StripeConfig/customers?q=${encodeURIComponent(stripeQuery)}`);
        if (res.ok) {
          const data = await res.json();
          setStripeResults(Array.isArray(data) ? data : []);
        }
      } catch {
        setStripeResults([]);
      } finally {
        setStripeSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [stripeQuery]);

  function selectStripeCustomer(c: { id: string; name: string; email: string }) {
    setStripeCustomerId(c.id);
    setCustomerName(c.name);
    setCustomerEmail(c.email);
    setStripeQuery("");
    setStripeResults([]);
  }

  function loadTemplate(id: number | "") {
    setSelectedTemplateId(id);
    if (id === "") return;
    const tmpl = templates.find((t) => t.id === Number(id));
    if (tmpl) {
      setBody(tmpl.body);
      setNetTerms(tmpl.netTerms);
    }
  }

  const monthlyValue = useManualMonthly ? manualMonthly : lineItemsTotal(lineItems);

  const placeholders: Record<string, string> = {
    customer_name: customerName || "Customer Name",
    customer_company: customerCompany || "Customer Company",
    start_date: serviceStartDate || "Start Date",
    end_date: serviceEndDate || "",
    end_date_clause: serviceEndDate ? ` through ${serviceEndDate}` : "",
    monthly_value: fmtCurrency(monthlyValue).replace("$", ""),
    net_terms: netTerms,
  };

  const previewBody = fillPlaceholders(body, placeholders);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  async function buildPayload(): Promise<ContractPayload> {
    return {
      templateId: selectedTemplateId ? Number(selectedTemplateId) : undefined,
      title,
      body,
      customerName,
      customerEmail,
      customerCompany: customerCompany || undefined,
      stripeCustomerId: stripeCustomerId || undefined,
      totalMonthlyValue: monthlyValue,
      setupFee: setupFee > 0 ? setupFee : undefined,
      netTerms,
      serviceStartDate,
      serviceEndDate: serviceEndDate || undefined,
      lineItemsJson: serializeLineItems(lineItems),
    };
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      const payload = await buildPayload();
      const res = await api.messaging.post("Contract", payload);
      if (res.ok) {
        onCreated();
        onClose();
      }
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndSend() {
    setSaving(true);
    try {
      const payload = await buildPayload();
      const res = await api.messaging.post("Contract", payload);
      if (res.ok) {
        const contract = await res.json();
        const sendRes = await api.messaging.post(`Contract/${contract.id}/send`, {});
        if (sendRes.ok) {
          const origin = window.location.origin;
          const link = `${origin}/sign/${contract.signingToken ?? contract.id}`;
          setSigningLink(link);
          onCreated();
        }
      }
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  function copySigningLink() {
    if (!signingLink) return;
    navigator.clipboard.writeText(signingLink).then(() => {
      showToast("Signing link copied!");
    });
  }

  // ── Step validation ─────────────────────────────────────────────────
  const step1Valid = customerName.trim() && customerEmail.trim();
  const step2Valid = title.trim() && body.trim();
  const step3Valid = serviceStartDate && monthlyValue >= 0;

  // ── Render steps ────────────────────────────────────────────────────

  function renderStep1() {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-500">Enter customer info or link to an existing Stripe customer.</p>

        {/* Stripe search */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Link to Stripe Customer (optional)
          </label>
          <div className="relative">
            <input
              type="text"
              value={stripeQuery}
              onChange={(e) => setStripeQuery(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              placeholder="Search by name or email..."
            />
            {stripeSearching && (
              <div className="absolute right-3 top-2.5 text-slate-400 text-xs">Searching...</div>
            )}
            {stripeResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                {stripeResults.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => selectStripeCustomer(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <div className="font-medium text-slate-800">{c.name}</div>
                    <div className="text-slate-400 text-xs">{c.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {stripeCustomerId && (
            <p className="text-xs text-green-600 mt-1">
              Stripe customer linked: <code>{stripeCustomerId}</code>
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Customer Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              placeholder="Jane Smith"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Customer Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              placeholder="jane@acme.com"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            value={customerCompany}
            onChange={(e) => setCustomerCompany(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
            placeholder="Acme Corp"
          />
        </div>
      </div>
    );
  }

  function renderStep2() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Contract Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              placeholder="Master Services Agreement — Acme Corp"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Load Template
            </label>
            <select
              value={selectedTemplateId}
              onChange={(e) => loadTemplate(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
            >
              <option value="">— Select template —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-slate-700">
              Contract Body (Markdown) <span className="text-red-400">*</span>
            </label>
            <button
              onClick={() => setShowBodyPreview((p) => !p)}
              className="text-xs text-primary hover:underline"
            >
              {showBodyPreview ? "Hide Preview" : "Show Preview"}
            </button>
          </div>
          <p className="text-xs text-slate-400 mb-2">
            Placeholders:{" "}
            {["{{customer_name}}", "{{customer_company}}", "{{start_date}}", "{{end_date}}", "{{monthly_value}}", "{{net_terms}}"].map((p) => (
              <code key={p} className="bg-slate-100 px-1 rounded mr-1">{p}</code>
            ))}
          </p>
          <div className={`grid gap-4 ${showBodyPreview ? "grid-cols-2" : "grid-cols-1"}`}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-hover resize-none"
            />
            {showBodyPreview && (
              <div
                className="border border-slate-200 rounded-lg p-4 overflow-y-auto text-sm text-slate-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(fillPlaceholders(body, placeholders)) }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderStep3() {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Service Start Date <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              value={serviceStartDate}
              onChange={(e) => setServiceStartDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Service End Date (optional)
            </label>
            <input
              type="date"
              value={serviceEndDate}
              onChange={(e) => setServiceEndDate(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Net Terms
            </label>
            <select
              value={netTerms}
              onChange={(e) => setNetTerms(e.target.value as NetTerms)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
            >
              {NET_TERMS_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Setup Fee (optional)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={setupFee || ""}
                onChange={(e) => setSetupFee(Number(e.target.value))}
                className="w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-700">
              Recurring Line Items
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
              <input
                type="checkbox"
                checked={useManualMonthly}
                onChange={(e) => setUseManualMonthly(e.target.checked)}
              />
              Override monthly value manually
            </label>
          </div>
          <LineItemEditor items={lineItems} onChange={setLineItems} />

          {useManualMonthly && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Manual Monthly Value Override
              </label>
              <div className="relative w-48">
                <span className="absolute left-3 top-2.5 text-slate-400 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={manualMonthly || ""}
                  onChange={(e) => setManualMonthly(Number(e.target.value))}
                  className="w-full border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderStep4() {
    const parsedItems = lineItems.filter((i) => i.description.trim());

    return (
      <div className="space-y-5">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Contract Summary</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-slate-500">Customer</dt>
              <dd className="font-medium text-slate-800">{customerName}</dd>
              <dd className="text-slate-500">{customerEmail}</dd>
              {customerCompany && <dd className="text-slate-500">{customerCompany}</dd>}
            </div>
            <div>
              <dt className="text-slate-500">Service Period</dt>
              <dd className="font-medium text-slate-800">
                {serviceStartDate}
                {serviceEndDate && ` – ${serviceEndDate}`}
              </dd>
              <dt className="text-slate-500 mt-2">Net Terms</dt>
              <dd className="font-medium text-slate-800">{netTerms}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Monthly Value</dt>
              <dd className="text-xl font-bold text-primary">{fmtCurrency(monthlyValue)}</dd>
            </div>
            {setupFee > 0 && (
              <div>
                <dt className="text-slate-500">Setup Fee</dt>
                <dd className="font-medium text-slate-800">{fmtCurrency(setupFee)}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Line items */}
        {parsedItems.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Line Items</h4>
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-slate-600">Description</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Qty</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Unit</th>
                  <th className="text-right px-3 py-2 font-medium text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {parsedItems.map((row, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">{row.description}</td>
                    <td className="px-3 py-2 text-right">{row.qty}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(row.unitPrice)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtCurrency(row.qty * row.unitPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Contract preview */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Contract Preview</h4>
          <div
            className="border border-slate-200 rounded-lg p-5 max-h-64 overflow-y-auto text-sm text-slate-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(previewBody) }}
          />
        </div>

        {/* Signing link (after send) */}
        {signingLink && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-green-700 mb-2">Contract sent for signature!</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={signingLink}
                className="flex-1 border border-green-300 rounded-lg px-3 py-2 text-xs bg-white text-slate-700"
              />
              <button
                onClick={copySigningLink}
                className="px-3 py-2 text-xs font-medium bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Copy
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const canGoNext =
    step === 1 ? !!step1Valid :
    step === 2 ? !!step2Valid :
    step === 3 ? !!step3Valid :
    true;

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 bg-slate-800 text-white text-sm px-4 py-3 rounded-lg shadow-lg z-[60]">
          {toast}
        </div>
      )}

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">New Contract</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 pt-5 pb-3">
            <StepIndicator step={step} total={4} />
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <button
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 1}
              className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
            >
              Back
            </button>
            <div className="flex gap-3">
              {step < 4 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canGoNext}
                  className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary disabled:opacity-40"
                >
                  Next
                </button>
              ) : signingLink ? (
                <button
                  onClick={onClose}
                  className="px-5 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSaveDraft}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save as Draft"}
                  </button>
                  <button
                    onClick={handleSaveAndSend}
                    disabled={saving}
                    className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary disabled:opacity-50"
                  >
                    {saving ? "Sending..." : "Save & Send for Signature"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
